import React from 'react';
import { RouteComponentProps, Link as InternalLink } from '@reach/router';
import { SimpleGrid, Box, Link as ExternalLink } from '@chakra-ui/react';
import { Account20, ChartBar20 } from '@carbon/icons-react';
import { camelCase } from 'change-case';

import { useProducts } from '#core/Products';
import { useInstitution } from '#core/Institution';
import { useEnvironment } from '#core/Environment';
import { usePermissions } from '#core/Permissions';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { EventTrackingSection } from '#core/Tracking';
import Heading from '#coreComponents/Heading';
import Section from '#coreComponents/Section';
import AccessDeniedErrorScreen from '#coreComponents/ErrorBoundary/AccessDeniedErrorScreen';
import ProductCard from './ProductCard';
import DashboardTopbar from './DashboardTopbar';
import OtherServices from './OtherServices';
import Sparkle20 from '../assets/SparkleIcon';

const footerLinks = [
  {
    text: 'Cookie Policy',
    url: 'https://www.timeshighereducation.com/cookie-policy',
  },
  {
    text: 'Privacy Policy',
    url: 'https://www.timeshighereducation.com/privacy-policy',
  },
  {
    text: 'Accessibility Statement',
    url:
      'https://www.timeshighereducation.com/content/online-accessibility-statement',
  },
  {
    text: 'Terms and Conditions',
    url: 'https://www.timeshighereducation.com/terms-and-conditions/datapoints',
  },
  {
    text: 'FAQs',
    url: '/datapoints/faqs',
  },
];

const wurOverviewFree = {
  name: 'WUR Dashboard',
  description:
    'Access the full dataset behind THE’s influential World University Rankings',
  path: 'wur',
  icon: ChartBar20,
  color: 'redOrange',
};

const Dashboard: React.FC<RouteComponentProps> = () => {
  const flags = useFlags();
  const { hasResourcePermissionNew } = usePermissions();
  const { products } = useProducts();
  const { institution } = useInstitution();
  const { reputationUrl, theInsightsUrl } = useEnvironment();
  const { insightsPreviewUrl } = institution;

  const hasReputationCoreRole = hasResourcePermissionNew({
    action: 'reputation_core_new:view_tableau_dashboard',
    institutionId: institution.institutionId,
  });

  const hasReputationAdvancedRole = hasResourcePermissionNew({
    action: 'reputation_advanced_new:view_tableau_dashboard',
    institutionId: institution.institutionId,
  });

  const hasTheInsightsRole = hasResourcePermissionNew({
    action: 'the_insights:view_dashboard',
    institutionId: institution.institutionId,
  });

  const canViewDashboard = hasResourcePermissionNew({
    action: 'datapoints:viewPage',
    institutionId: institution.institutionId,
  });

  const canViewFreeWurDashboard = hasResourcePermissionNew({
    year: new Date().getFullYear(),
    action: 'dataplus:viewPageFree',
    institutionId: institution.institutionId,
  });

  if (!canViewDashboard) {
    return (
      <AccessDeniedErrorScreen text="You do not have access to the DataPoints platform." />
    );
  }

  const showNewReputation = flags[camelCase('NewReputation')];

  const showTheInsights = flags[camelCase('showTheInsightsMenuOption')];

  const showWurDashboardPreview = flags[camelCase('showWurDashboardPreview')];

  return (
    <EventTrackingSection section="dashboard-page">
      <DashboardTopbar />

      <SimpleGrid gridGap={7} columns={4}>
        <Box gridColumn={{ base: '1 / span 4', lg: '1 / span 4' }}>
          <EventTrackingSection section="product-cards">
            <Section>
              <Heading data-testid="section-heading" as="h2" mt="16" mb="8">
                Your services
              </Heading>
              <SimpleGrid gridGap={6} columns={{ base: 1, md: 3 }}>
                {products.map(
                  (product) =>
                    !product.productConfig.isDevProduct && (
                      <ProductCard
                        key={product.name}
                        name={product.name}
                        icon={product.icon}
                        description={product.description}
                        color={product.color}
                        path={product?.pages[0].url}
                      />
                    ),
                )}

                {canViewFreeWurDashboard &&
                  insightsPreviewUrl &&
                  showWurDashboardPreview && (
                    <ProductCard
                      key={wurOverviewFree.name}
                      name={wurOverviewFree.name}
                      icon={wurOverviewFree.icon}
                      description={wurOverviewFree.description}
                      color={wurOverviewFree.color}
                      path={insightsPreviewUrl}
                      isExternalLink
                    />
                  )}

                {hasReputationCoreRole && showNewReputation && (
                  <ProductCard
                    key="Reputation Core"
                    name="Reputation Core"
                    label="NEW"
                    icon={Account20}
                    description="Track your global reputation trends, WUR rankings, and benchmark your institution's performance in teaching and research"
                    color="electricViolet"
                    path={`${reputationUrl}dashboard/reputation-core/${institution.institutionId}`}
                    isExternalLink={true}
                  />
                )}

                {hasReputationAdvancedRole && showNewReputation && (
                  <ProductCard
                    key="Reputation Advanced"
                    name="Reputation Advanced"
                    label="NEW"
                    icon={Account20}
                    description="Track your global reputation trends, WUR rankings, and benchmark your institution's performance in teaching and research"
                    color="electricViolet"
                    path={`${reputationUrl}dashboard/reputation-advanced/${institution.institutionId}`}
                    isExternalLink={true}
                  />
                )}

                {hasTheInsightsRole && showTheInsights && (
                  <ProductCard
                    key="THE Insights"
                    name="THE Insights"
                    icon={Sparkle20}
                    description="Access deep-dive analysis of rankings to understand performance, benchmark against peers and build action plans for improvement"
                    color="electricViolet"
                    path={`${theInsightsUrl}/auth/signin`}
                    isExternalLink={true}
                  />
                )}
              </SimpleGrid>
            </Section>
          </EventTrackingSection>
        </Box>

        <Box gridColumn={{ base: '1 / span 4', lg: '1 / span 4' }}>
          <EventTrackingSection section="other-services">
            <OtherServices />
          </EventTrackingSection>
        </Box>
      </SimpleGrid>

      <Box mt={8} color="gray.500" textAlign="center">
        {footerLinks.map(({ text, url }, index) => (
          <Box
            key={url}
            display="inline-block"
            _before={
              index === 0
                ? {}
                : {
                    content: '"|"',
                    p: 2,
                  }
            }
          >
            {url.startsWith('/') && !url.startsWith('//') ? (
              <InternalLink to={url}>{text}</InternalLink>
            ) : (
              <ExternalLink href={url} target="_blank" cursor="pointer">
                {text}
              </ExternalLink>
            )}
          </Box>
        ))}
      </Box>
    </EventTrackingSection>
  );
};

export default Dashboard;
